import * as React from "react";
import {
  Box,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import StraightenIcon from "@mui/icons-material/Straighten";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import TireRepairIcon from "@mui/icons-material/TireRepair";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InventoryIcon from '@mui/icons-material/Inventory';
import ChatIcon from '@mui/icons-material/Chat';
import AddIcon from "@mui/icons-material/Add";
import { AppProvider } from "../../ContextApi/AppProvider";
import { useContext } from "react";
import { CadastroService } from "../../Services/CadastrosService";
import { TipoCadastro, TipoCadastroDescricao } from "../Shared/Enums/TipoCadastro";
import DataItem from "../Shared/Interfaces/DataItem";
import colors from "../../theme/colors";
import Item from "../Shared/Style/StyledItem";

interface DataStructure extends Record<TipoCadastro, DataItem[]> {}

export default function Generico() {
  const contextApi = useContext(AppProvider);
  const [data, setData] = React.useState<DataStructure>();
  const cadastroService = new CadastroService();
  const [selectedCategory, setSelectedCategory] = React.useState<TipoCadastro>();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [newItemDescription, setNewItemDescription] = React.useState("");

  const handleItemClick = (category: TipoCadastro) => setSelectedCategory(category);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setNewItemDescription("");
  };

  const handleAddNewItem = async () => {
    if (selectedCategory && newItemDescription.trim()) {
      const newItem = { tipoCadastro: selectedCategory, descricao: newItemDescription };
      try {
        const result = await cadastroService.postItem(newItem);
        result.status === 200
          ? contextApi.showMessage(`Item Cadastrado: ${newItem.descricao}`, true, "success")
          : contextApi.showMessage(`Erro ao cadastrar: ${newItem.descricao}`, true, "error");

        getAllCadastros();
        closeModal();
      } catch (error) {
        console.error("Erro ao adicionar item:", error);
      }
    }
  };

  const handleDeleteItem = async (item: DataItem) => {
    if (!selectedCategory) return;

    const newItem = { tipoCadastro: selectedCategory, descricao: item.descricao };
    try {
      const result = await cadastroService.postDeleteItem(newItem);
      result.status === 200
        ? contextApi.showMessage(`Item Deletado: ${newItem.descricao}`, true, "success")
        : contextApi.showMessage(`Erro ao deletar: ${newItem.descricao}`, true, "error");

      getAllCadastros();
    } catch (error) {
      console.error("Erro ao deletar item:", error);
    }
  };

  React.useEffect(() => {
    getAllCadastros();
  }, []);

  const getAllCadastros = async () => {
    await Promise.all([getMedidas(), getTipoDePneu(), getMarcas(), getTiposDeProdutos(), getCanaisDeComunicacao()]);
  };

  const getMedidas = async () => {
    try {
      const response = await cadastroService.getAll(`/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.Medidas}`);
      setData((prev) => ({
        ...prev,
        Medidas: response.data || [],
        TiposDePneus: prev?.TiposDePneus || [],
        Marcas: prev?.Marcas || [],
        FormaDePagamento: prev?.FormaDePagamento || [],
        TiposDeProdutos: prev?.TiposDeProdutos || [],
        CanaisDeComunicacao: prev?.CanaisDeComunicacao || [],
      }));
    } catch (e) {
      console.error("Erro ao buscar Medidas:", e);
    }
  };

  const getTipoDePneu = async () => {
    try {
      const response = await cadastroService.getAll(`/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.TiposDePneus}`);
      setData((prev) => ({
        ...prev,
        Medidas: prev?.Medidas || [],
        TiposDePneus: response.data || [],
        Marcas: prev?.Marcas || [],
        FormaDePagamento: prev?.FormaDePagamento || [],
        TiposDeProdutos: prev?.TiposDeProdutos || [],
        CanaisDeComunicacao: prev?.CanaisDeComunicacao || [],
      }));
    } catch (e) {
      console.error("Erro ao buscar Tipos de Pneu:", e);
    }
  };

  const getTiposDeProdutos = async () => {
    try {
      const response = await cadastroService.getAll(`/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.TiposDeProdutos}`);
      setData((prev) => ({
        ...prev,
        Medidas: prev?.Medidas || [],
        TiposDePneus: prev?.TiposDePneus || [],
        Marcas: prev?.Marcas || [],
        FormaDePagamento: prev?.FormaDePagamento || [],
        TiposDeProdutos: response.data || [],
        CanaisDeComunicacao: prev?.CanaisDeComunicacao || [],
      }));
    } catch (e) {
      console.error("Erro ao buscar Tipos de Pneu:", e);
    }
  };

  const getMarcas = async () => {
    try {
      const response = await cadastroService.getAll(`/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.Marcas}`);
      setData((prev) => ({
        ...prev,
        Medidas: prev?.Medidas || [],
        TiposDePneus: prev?.TiposDePneus || [],
        Marcas: response.data || [],
        FormaDePagamento: prev?.FormaDePagamento || [],
        TiposDeProdutos: prev?.TiposDeProdutos || [],
        CanaisDeComunicacao: prev?.CanaisDeComunicacao || [],
      }));
    } catch (e) {
      console.error("Erro ao buscar Marcas:", e);
    }
  };

  const getCanaisDeComunicacao = async () => {
    try {
      const response = await cadastroService.getAll(`/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.CanaisDeComunicacao}`);
      setData((prev) => ({
        ...prev,
        Medidas: prev?.Medidas || [],
        TiposDePneus: prev?.TiposDePneus || [],
        Marcas: prev?.Marcas || [],
        FormaDePagamento: prev?.FormaDePagamento || [],
        TiposDeProdutos: prev?.TiposDeProdutos || [],
        CanaisDeComunicacao: response.data || [],
      }));
    } catch (e) {
      console.error("Erro ao buscar Canais de Comunicação:", e);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Cabeçalho e Descrição */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                ⚙️ Cadastros Genéricos
              </h4>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Gestão de configurações e parâmetros do sistema
              </Typography>
            </Paper>
          </Grid>

          {/* Linha 2 - Categorias de Cadastro */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📋 Categorias de Cadastro</h4>
              
              <Grid container spacing={2} justifyContent="center">
                {[TipoCadastro.Medidas, TipoCadastro.TiposDePneus, TipoCadastro.Marcas, TipoCadastro.TiposDeProdutos, TipoCadastro.CanaisDeComunicacao].map((tipo, idx) => (
                  <Grid key={idx} item xs={6} sm={4} md={2}>
                    <Item onClick={() => handleItemClick(tipo)}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        p: 1
                      }}>
                        {tipo === TipoCadastro.Medidas && (
                          <StraightenIcon
                            sx={{ 
                              fontSize: "3em", 
                              color: selectedCategory === tipo ? colors.accent : colors.primary,
                              mb: 1
                            }}
                          />
                        )}
                        {tipo === TipoCadastro.TiposDePneus && (
                          <TireRepairIcon
                            sx={{ 
                              fontSize: "3em", 
                              color: selectedCategory === tipo ? colors.accent : colors.primary,
                              mb: 1
                            }}
                          />
                        )}
                        {tipo === TipoCadastro.Marcas && (
                          <BookmarksIcon
                            sx={{ 
                              fontSize: "3em", 
                              color: selectedCategory === tipo ? colors.accent : colors.primary,
                              mb: 1
                            }}
                          />
                        )}
                        {tipo === TipoCadastro.TiposDeProdutos && (
                          <InventoryIcon
                            sx={{ 
                              fontSize: "3em", 
                              color: selectedCategory === tipo ? colors.accent : colors.primary,
                              mb: 1
                            }}
                          />
                        )}
                        {tipo === TipoCadastro.CanaisDeComunicacao && (
                          <ChatIcon
                            sx={{ 
                              fontSize: "3em", 
                              color: selectedCategory === tipo ? colors.accent : colors.primary,
                              mb: 1
                            }}
                          />
                        )}
                        <Typography variant="body2" sx={{ 
                          color: selectedCategory === tipo ? colors.accent : colors.primary,
                          fontWeight: 'bold',
                          textAlign: 'center'
                        }}>
                          {TipoCadastroDescricao[tipo]}
                        </Typography>
                      </Box>
                    </Item>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Linha 3 - Gestão da Categoria Selecionada */}
          {selectedCategory && (
            <Grid item xs={12}>
              <Paper sx={{ p: 1, mb: 1 }}>
                <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>
                  🛠️ Gestão de {TipoCadastroDescricao[selectedCategory]}
                </h4>
                
                {/* Cabeçalho da Seção */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                    <Typography variant="h6" sx={{ color: colors.primary, fontWeight: 'bold' }}>
                      {TipoCadastroDescricao[selectedCategory]}
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={openModal}
                      startIcon={<AddIcon />}
                      sx={{ height: '36px' }}
                    >
                      Novo Item
                    </Button>
                  </Box>
                </Grid>

                {/* Lista de Itens */}
                <Grid container spacing={2}>
                  {data?.[selectedCategory as keyof DataStructure]?.map((item) => (
                    <Grid key={item.id} item xs={12} sm={6} md={4} lg={3}>
                      <Paper sx={{ 
                        p: 2, 
                        backgroundColor: "#F5F5F5", 
                        borderRadius: 2,
                        boxShadow: 1,
                        border: '1px solid #e0e0e0',
                        height: '100%'
                      }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="body1" sx={{ 
                            fontWeight: 'bold',
                            color: colors.primary,
                            flex: 1,
                            mr: 1
                          }}>
                            {item.descricao}
                          </Typography>
                          <DeleteOutlineIcon
                            fontSize="medium"
                            sx={{ 
                              color: "#d32f2f", 
                              cursor: "pointer",
                              '&:hover': {
                                color: "#b71c1c"
                              }
                            }}
                            onClick={() => handleDeleteItem(item)}
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Modal de Adição */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "auto" },
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle sx={{ color: colors.primary, fontWeight: 'bold' }}>
          Adicionar {selectedCategory ? TipoCadastroDescricao[selectedCategory] : 'Item'}
        </DialogTitle>
        <DialogContent sx={{ overflowY: "auto" }}>
          <TextField
            autoFocus
            margin="dense"
            label="Descrição"
            fullWidth
            variant="outlined"
            size="small"
            value={newItemDescription}
            onChange={(e) => setNewItemDescription(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleAddNewItem} color="primary" variant="contained">
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}